import { useState, type ReactNode } from 'react'
import { useWarehousePurchaseOrder } from '@/api/warehouse-sourcing'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { LoadingButton } from '@/components/loading-button'
import { Check, X, Edit2, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { useAddLineItem, useDeleteLineItem } from '@/features/purchase-orders/hooks'
import { CatalogLineSearch, type CatalogLineProduct } from '@/components/products/catalog-line-search'
import { ActionPopover } from '@/components/action-popover'
import {
  LineKindBadge,
  LineKindMarker,
  POLineItemProductCell,
} from '@/components/po-line-item-product-cell'
import { cn, formatMoney, formatStatus } from '@/lib/utils'
import type { components } from '@/api/schema'
import type { PurchaseOrderLineItemRead } from './types'
import {
  EditableDiscountCell,
  EditableUnitPriceCell,
  EditableQtyCell,
  EditableListPriceCell,
  EditableLineItemProductCell,
} from './line-item-editors'
import { poLineDisplayNumber, isManufacturerSurchargeLine } from './line-item-policy'
import { POTotals } from './subtotal-discount'
import { ItemStatusButton, ItemStatusPopover } from './item-statuses'
import { itemStatusesById } from './item-status-model'

export function LineItemsCard({ po }: { po: components['schemas']['PurchaseOrderDetailRead'] }) {
  // Pulled from our own warehouse: a line costs what we paid for its lot, so the maker's default
  // discount next to it is noise (USA-1574).
  const fromWarehouse = useWarehousePurchaseOrder(po.uuid).data?.from_warehouse === true
  const [editMode, setEditMode] = useState(false)
  // 'search' finds the product in the catalog; 'manual' is the typed form for what it doesn't carry.
  const [addMode, setAddMode] = useState<'search' | 'manual' | null>(null)
  const showAddForm = addMode !== null
  const [formModel, setFormModel] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formQuantity, setFormQuantity] = useState('1')
  const [formUnitPrice, setFormUnitPrice] = useState('')
  const [formSellingUnit, setFormSellingUnit] = useState('')
  const [formListPrice, setFormListPrice] = useState('')
  const [formNetPrice, setFormNetPrice] = useState('')
  const [formDiscountDisplay, setFormDiscountDisplay] = useState('')

  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  // Which items the status popover is open for, and which trigger it hangs from.
  const [statusTarget, setStatusTarget] = useState<{ ids: number[]; anchor: string } | null>(null)
  const statusTargets = statusTarget?.ids

  const addMutation = useAddLineItem(po.uuid)
  const deleteMutation = useDeleteLineItem(po.uuid)

  const resetForm = () => {
    setFormModel('')
    setFormDescription('')
    setFormQuantity('1')
    setFormUnitPrice('')
    setFormSellingUnit('')
    setFormListPrice('')
    setFormNetPrice('')
    setFormDiscountDisplay('')
  }

  const errorDetail = (err: unknown, fallback: string) =>
    err && typeof err === 'object' && 'detail' in err
      ? String((err as { detail: unknown }).detail)
      : fallback

  const quantityNum = parseInt(formQuantity, 10)
  const canSubmit =
    formModel.trim().length > 0 &&
    formDescription.trim().length > 0 &&
    Number.isFinite(quantityNum) &&
    quantityNum >= 1 &&
    formUnitPrice.trim().length > 0

  const handleAddProduct = (product: CatalogLineProduct, quantity: number) => {
    addMutation.mutate(
      { product_uuid: product.uuid, quantity },
      {
        onSuccess: () => {
          toast.success('Line item added')
          setAddMode(null)
        },
        onError: (err) => toast.error(errorDetail(err, 'Failed to add line item')),
      },
    )
  }

  const handleAdd = () => {
    if (!canSubmit) return
    addMutation.mutate(
      {
        model: formModel.trim(),
        description: formDescription.trim(),
        quantity: quantityNum,
        unit_price: formUnitPrice.trim(),
        selling_unit: formSellingUnit.trim() || null,
        list_price: formListPrice.trim() || null,
        net_price: formNetPrice.trim() || null,
        discount_display: formDiscountDisplay.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Line item added')
          resetForm()
          setAddMode(null)
        },
        onError: (err) => toast.error(errorDetail(err, 'Failed to add line item')),
      },
    )
  }

  const removeLine = (lineItem: PurchaseOrderLineItemRead, close: () => void) => {
    deleteMutation.mutate(
      { lineItemId: lineItem.id },
      {
        onSuccess: () => {
          toast.success('Line item removed')
          close()
        },
        onError: (err) => toast.error(errorDetail(err, 'Failed to remove line item')),
      },
    )
  }

  // The PO's manufacturer is in its details; name it on a line only when that line differs.
  const showLineManufacturer = (li: PurchaseOrderLineItemRead) => {
    const name = li.manufacturer_name || li.product?.manufacturer_name
    return !!name && name !== po.manufacturer?.short_name && name !== po.manufacturer?.name
  }
  const hasItems = (po.line_items?.length ?? 0) > 0
  const colSpan = editMode ? 7 : 6
  const statuses = itemStatusesById(po.status_summary)
  const isCancelled = (li: PurchaseOrderLineItemRead) => statuses.get(li.id)?.key === 'cancelled'
  // An item's status shows beside its SKU when it needs attention (backordered, delayed, cancelled),
  // differs from most of the PO's items, or staff set it; the PO's own Order and Shipping
  // statuses already say what the rest are doing.
  const attentionIds = new Set((po.status_summary?.attention ?? []).map((item) => item.line_item_id))
  const keyCounts = new Map<string, number>()
  for (const item of statuses.values()) keyCounts.set(item.key, (keyCounts.get(item.key) ?? 0) + 1)
  const usualKey = [...keyCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]
  const showStatus = (li: PurchaseOrderLineItemRead) => {
    const status = statuses.get(li.id)
    return (
      !!status &&
      (attentionIds.has(li.id) || status.key !== usualKey || status.source === 'manual')
    )
  }
  const toggle = (id: number, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  const allSelected = hasItems && po.line_items.every((li) => selected.has(li.id))
  const targets = po.line_items.filter((li) => statusTargets?.includes(li.id))
  // What cancelling the targets takes off the total: their lines and sub-lines not already cancelled.
  const cancelSubtotal = po.line_items
    .filter(
      (li) =>
        !isCancelled(li) &&
        (statusTargets?.includes(li.id) ||
          (li.parent_line_item_id != null && statusTargets?.includes(li.parent_line_item_id))),
    )
    .reduce((sum, li) => sum + Number(li.line_total || 0), 0)
  const percentOff =
    po.subtotal_discount?.discount_type === 'percent' ? Number(po.subtotal_discount.value) / 100 : 0
  const cancelAmount = cancelSubtotal * (1 - percentOff)
  const statusPopover = (ids: number[], anchor: string, trigger: ReactNode) => (
    <ItemStatusPopover
      poUuid={po.uuid}
      items={statusTarget?.anchor === anchor ? targets : []}
      poTotal={String(po.total)}
      cancelAmount={cancelAmount}
      trigger={trigger}
      open={statusTarget?.anchor === anchor}
      onOpenChange={(open) => setStatusTarget(open ? { ids, anchor } : null)}
      onSaved={() => setSelected(new Set())}
    />
  )
  const itemStatus = (li: PurchaseOrderLineItemRead, anchor: string) =>
    showStatus(li)
      ? statusPopover(
          [li.id],
          `${anchor}-${li.id}`,
          <ItemStatusButton status={statuses.get(li.id)} model={li.model} />,
        )
      : null

  return (
    <Card className="@container/po-lines gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {selected.size > 0 ? (
            <div className="flex min-w-0 items-center gap-2" role="group" aria-label="Selected items">
              <span className="whitespace-nowrap text-sm font-medium tabular-nums">
                {selected.size} selected
              </span>
              {statusPopover(
                [...selected],
                'selection',
                <Button size="sm" className="h-8">
                  Set status
                </Button>,
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => setSelected(new Set())}
              >
                Clear
              </Button>
            </div>
          ) : (
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Line Items
            </h3>
          )}
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            className="h-8"
            onClick={() => {
              setEditMode((v) => !v)
              if (editMode) {
                setAddMode(null)
                resetForm()
              }
            }}
          >
            {editMode ? (
              <>
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Done
              </>
            ) : (
              <>
                <Edit2 className="mr-1.5 h-3.5 w-3.5" />
                Edit items
              </>
            )}
          </Button>
        </div>

        {/* Desktop table */}
        <div data-po-line-items-layout="table" className="hidden @3xl/po-lines:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-px pr-0">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary align-middle"
                    aria-label="Select all items"
                    checked={allSelected}
                    disabled={!hasItems}
                    onChange={(event) =>
                      setSelected(
                        event.target.checked ? new Set(po.line_items.map((li) => li.id)) : new Set(),
                      )
                    }
                  />
                </TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="w-px text-right">Qty</TableHead>
                <TableHead className="w-px text-right whitespace-nowrap">List · Discount</TableHead>
                <TableHead className="w-px text-right">Unit</TableHead>
                <TableHead className="w-px text-right">Total</TableHead>
                {editMode && <TableHead className="w-[60px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {hasItems ? (
                po.line_items.map((li) => (
                  <TableRow
                    key={li.id}
                    id={`po-line-${li.id}`}
                    data-po-line-item
                    data-state={selected.has(li.id) ? 'selected' : undefined}
                    className={cn(
                      li.parent_line_item_id && 'bg-muted/25',
                      isCancelled(li) && 'text-muted-foreground',
                    )}
                  >
                    <TableCell className="w-px pr-0 align-top">
                      <input
                        type="checkbox"
                        className="mt-1.5 size-4 accent-primary"
                        aria-label={`Select ${li.model}`}
                        checked={selected.has(li.id)}
                        onChange={(event) => toggle(li.id, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell className="min-w-0 whitespace-normal">
                      <div className="flex items-start gap-2">
                        <div className="flex min-w-5 items-center gap-1 pt-1 text-xs font-semibold text-muted-foreground tabular-nums">
                          {/* A sub-line's indent holds its (i)/(W) marker, left of the chevron. */}
                          {li.parent_line_item_id && (
                            <span data-sub-line-gutter="true" className="flex w-5 shrink-0">
                              <LineKindMarker kind={li.line_kind} />
                            </span>
                          )}
                          {li.parent_line_item_id && <ChevronRight className="h-3 w-3" />}
                          {poLineDisplayNumber(li)}
                        </div>
                        <div className="min-w-0 flex-1">
                          {li.line_kind !== 'main' && (
                            <div className="mb-1 flex flex-wrap items-center gap-1.5 empty:hidden">
                              <LineKindBadge
                                kind={li.line_kind}
                                label={formatStatus(li.line_kind)}
                                className="text-[10px]"
                              />
                            </div>
                          )}
                          {editMode ? (
                            <EditableLineItemProductCell
                              poUuid={po.uuid}
                              lineItem={li}
                              showManufacturer={showLineManufacturer(li)}
                              modelSuffix={itemStatus(li, 'row')}
                            />
                          ) : (
                            <POLineItemProductCell
                              model={li.model}
                              modelSuffix={itemStatus(li, 'row')}
                              description={li.description}
                              productUuid={li.product?.uuid}
                              imageUrl={li.product?.image_url}
                              manufacturerName={
                                li.manufacturer_name || li.product?.manufacturer_name
                              }
                              manufacturerUuid={li.product?.manufacturer_uuid}
                              showManufacturer={showLineManufacturer(li)}
                              collapseLongDescription
                              collapsedDescriptionLines={1}
                            />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {isManufacturerSurchargeLine(li) ? (
                        li.quantity
                      ) : (
                        <EditableQtyCell poUuid={po.uuid} lineItem={li} />
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {isManufacturerSurchargeLine(li) ? (
                        '-'
                      ) : (
                        <div className="flex flex-col items-end">
                          <EditableListPriceCell poUuid={po.uuid} lineItem={li} />
                          <span className="text-xs text-muted-foreground">
                            <EditableDiscountCell poUuid={po.uuid} lineItem={li} hideDefault={fromWarehouse} />
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <EditableUnitPriceCell poUuid={po.uuid} lineItem={li} />
                    </TableCell>
                    <TableCell
                      className={cn(
                        'whitespace-nowrap text-right font-medium tabular-nums',
                        isCancelled(li) && 'line-through',
                      )}
                    >
                      {formatMoney(li.line_total)}
                    </TableCell>
                    {editMode && (
                      <TableCell className="text-right">
                        <RemoveLineButton
                          lineItem={li}
                          className="h-10 w-10 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          pending={deleteMutation.isPending && deleteMutation.variables?.lineItemId === li.id}
                          onRemove={(close) => removeLine(li, close)}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center text-muted-foreground">
                    No line items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div data-po-line-items-layout="cards" className="space-y-2 @3xl/po-lines:hidden">
          {hasItems ? (
            po.line_items.map((li) => (
              <div
                key={li.id}
                id={`po-line-${li.id}-card`}
                data-po-line-item
                className={cn(
                  'rounded-lg border p-3',
                  li.parent_line_item_id ? 'ml-5 bg-muted/25' : 'bg-card',
                  isCancelled(li) && 'text-muted-foreground',
                )}
              >
                <div className="flex gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-5 shrink-0 accent-primary"
                    aria-label={`Select ${li.model}`}
                    checked={selected.has(li.id)}
                    onChange={(event) => toggle(li.id, event.target.checked)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-1 text-xs font-semibold text-muted-foreground">
                      {li.parent_line_item_id && (
                        <span data-sub-line-gutter="true" className="flex w-4 shrink-0">
                          <LineKindMarker kind={li.line_kind} />
                        </span>
                      )}
                      {li.parent_line_item_id && <ChevronRight className="h-3 w-3" />}
                      Line {poLineDisplayNumber(li)}
                      <LineKindBadge kind={li.line_kind} label={formatStatus(li.line_kind)} />
                    </div>
                    {editMode ? (
                      <EditableLineItemProductCell
                        poUuid={po.uuid}
                        lineItem={li}
                        showManufacturer={showLineManufacturer(li)}
                        imageClassName="h-12 w-12"
                        modelSuffix={itemStatus(li, 'card')}
                      />
                    ) : (
                      <POLineItemProductCell
                        model={li.model}
                        modelSuffix={itemStatus(li, 'card')}
                        description={li.description}
                        productUuid={li.product?.uuid}
                        imageUrl={li.product?.image_url}
                        manufacturerName={li.manufacturer_name || li.product?.manufacturer_name}
                        manufacturerUuid={li.product?.manufacturer_uuid}
                        showManufacturer={showLineManufacturer(li)}
                        collapseLongDescription
                        collapsedDescriptionLines={1}
                        imageClassName="h-12 w-12"
                      />
                    )}
                  </div>
                  {editMode && (
                    <RemoveLineButton
                      lineItem={li}
                      className="h-10 w-10 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      pending={deleteMutation.isPending && deleteMutation.variables?.lineItemId === li.id}
                      onRemove={(close) => removeLine(li, close)}
                    />
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Qty</span>
                    {isManufacturerSurchargeLine(li) ? (
                      li.quantity
                    ) : (
                      <EditableQtyCell poUuid={po.uuid} lineItem={li} />
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">List</span>
                    {isManufacturerSurchargeLine(li) ? (
                      '-'
                    ) : (
                      <EditableListPriceCell poUuid={po.uuid} lineItem={li} />
                    )}
                  </div>
                  <div className="flex min-w-0 justify-between gap-2 [overflow-wrap:anywhere]">
                    <span className="shrink-0 text-muted-foreground">Discount</span>
                    {isManufacturerSurchargeLine(li) ? (
                      '-'
                    ) : (
                      <EditableDiscountCell poUuid={po.uuid} lineItem={li} hideDefault={fromWarehouse} />
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Unit</span>
                    <EditableUnitPriceCell poUuid={po.uuid} lineItem={li} />
                  </div>
                  <div className="col-span-2 flex justify-between border-t pt-1.5">
                    <span className="text-muted-foreground">Total</span>
                    <span className={cn('text-sm font-semibold', isCancelled(li) && 'line-through')}>
                      {formatMoney(li.line_total)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">No line items</p>
          )}
        </div>

        {editMode && !showAddForm && (
          <div className="flex justify-start">
            <Button
              variant="outline"
              size="sm"
              className="h-10 w-full sm:w-auto"
              onClick={() => setAddMode('search')}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add line item
            </Button>
          </div>
        )}

        {editMode && addMode === 'search' && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium">Add line item</h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setAddMode(null)}
                aria-label="Cancel add line item"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <CatalogLineSearch
              manufacturer={po.manufacturer?.name}
              pending={addMutation.isPending}
              onAdd={handleAddProduct}
              onManual={() => setAddMode('manual')}
            />
          </div>
        )}

        {editMode && addMode === 'manual' && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium">New manual item</h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  resetForm()
                  setAddMode(null)
                }}
                aria-label="Cancel add line item"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="new-li-model" className="text-xs text-muted-foreground">
                  Model <span className="text-destructive">*</span>
                </Label>
                <InlineFieldInput
                  id="new-li-model"
                  value={formModel}
                  onChange={(e) => setFormModel(e.target.value)}
                  placeholder="e.g. ABC-1234"
                  className="w-full"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="new-li-description" className="text-xs text-muted-foreground">
                  Description <span className="text-destructive">*</span>
                </Label>
                <InlineFieldInput
                  id="new-li-description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Short description"
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-li-quantity" className="text-xs text-muted-foreground">
                  Quantity <span className="text-destructive">*</span>
                </Label>
                <InlineFieldInput
                  id="new-li-quantity"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={formQuantity}
                  onChange={(e) => setFormQuantity(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-li-unit-price" className="text-xs text-muted-foreground">
                  Unit price <span className="text-destructive">*</span>
                </Label>
                <InlineFieldInput
                  id="new-li-unit-price"
                  type="text"
                  inputMode="decimal"
                  value={formUnitPrice}
                  onChange={(e) => setFormUnitPrice(e.target.value)}
                  placeholder="e.g. 12.34"
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-li-selling-unit" className="text-xs text-muted-foreground">
                  Selling unit (optional)
                </Label>
                <InlineFieldInput
                  id="new-li-selling-unit"
                  value={formSellingUnit}
                  onChange={(e) => setFormSellingUnit(e.target.value)}
                  placeholder="e.g. EA, CS"
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-li-list-price" className="text-xs text-muted-foreground">
                  List price (optional)
                </Label>
                <InlineFieldInput
                  id="new-li-list-price"
                  type="text"
                  inputMode="decimal"
                  value={formListPrice}
                  onChange={(e) => setFormListPrice(e.target.value)}
                  placeholder="e.g. 24.99"
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-li-net-price" className="text-xs text-muted-foreground">
                  Net price (optional)
                </Label>
                <InlineFieldInput
                  id="new-li-net-price"
                  type="text"
                  inputMode="decimal"
                  value={formNetPrice}
                  onChange={(e) => setFormNetPrice(e.target.value)}
                  placeholder="e.g. 10.00"
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-li-discount-display" className="text-xs text-muted-foreground">
                  Discount (optional)
                </Label>
                <InlineFieldInput
                  id="new-li-discount-display"
                  value={formDiscountDisplay}
                  onChange={(e) => setFormDiscountDisplay(e.target.value)}
                  placeholder="e.g. 50/10"
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                className="h-10 w-full sm:w-auto"
                onClick={() => {
                  resetForm()
                  setAddMode(null)
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                className="h-10 w-full sm:w-auto"
                loading={addMutation.isPending}
                loadingText="Adding..."
                disabled={!canSubmit}
                onClick={handleAdd}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add line item
              </LoadingButton>
            </div>
          </div>
        )}

        {po.total != null && <POTotals po={po} editMode={editMode} />}
      </CardContent>

    </Card>
  )
}

/** The trash button on an editable line; it asks once, in a popover, before removing the line. */
function RemoveLineButton({
  lineItem,
  className,
  pending,
  onRemove,
}: {
  lineItem: PurchaseOrderLineItemRead
  className: string
  pending: boolean
  onRemove: (close: () => void) => void
}) {
  return (
    <ActionPopover
      align="end"
      trigger={
        <LoadingButton
          variant="ghost"
          size="icon"
          className={className}
          aria-label={`Remove line item ${poLineDisplayNumber(lineItem)}`}
          loading={pending}
        >
          <Trash2 className="h-4 w-4" />
        </LoadingButton>
      }
      title={`Remove item #${poLineDisplayNumber(lineItem)}: ${lineItem.model} (qty ${lineItem.quantity}, ${formatMoney(lineItem.line_total)})?`}
      confirmLabel="Remove"
      confirmVariant="destructive"
      pending={pending}
      pendingLabel="Removing..."
      onConfirm={(_note, close) => onRemove(close)}
    />
  )
}
